import { useMemo } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Activity, BarChart3 } from "lucide-react";

const STATUS_META = {
  "2xx": { label: "2xx OK", color: "#34d399" },
  "3xx": { label: "3xx Redir", color: "#38bdf8" },
  "4xx": { label: "4xx Client", color: "#fbbf24" },
  "5xx": { label: "5xx Server", color: "#f43f5e" },
  err: { label: "Errors", color: "#a855f7" },
};

const TooltipBox = ({ active, payload, label, suffix }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded border border-cyan-500/40 bg-[#04060A]/95 px-2.5 py-1.5 font-mono text-[11px]">
      <p className="text-slate-500">{label}{suffix}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color || p.fill }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export const ThroughputChart = ({ samples = [], statusCounts = {} }) => {
  const data = useMemo(
    () => samples.slice(-90).map((s) => ({ t: s.t, pps: s.pps, total: s.total })),
    [samples]
  );
  const statusData = useMemo(
    () => Object.entries(STATUS_META)
      .map(([k, m]) => ({ name: m.label, key: k, value: statusCounts[k] || 0, color: m.color }))
      .filter((d) => d.value > 0),
    [statusCounts]
  );
  const peak = data.reduce((m, d) => Math.max(m, d.pps), 0);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="throughput-charts">
      <div className="xl:col-span-2 rounded-lg border border-border bg-[#0B0F18] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
          <Activity className="h-3.5 w-3.5 text-cyan-400" />
          <span className="font-mono text-xs uppercase tracking-wider text-slate-400">Throughput · pages/sec</span>
          <span className="ml-auto font-mono text-[11px] text-cyan-400 tabular-nums">peak {peak}/s</span>
        </div>
        <div className="h-[168px] px-1 py-2" data-testid="throughput-pps-chart">
          {data.length === 0 ? (
            <p className="h-full grid place-items-center font-mono text-xs text-slate-600">// no samples yet — launch a crawl</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="ppsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="t" tick={{ fill: "#475569", fontSize: 10, fontFamily: "monospace" }}
                  tickLine={false} axisLine={{ stroke: "#1e293b" }} />
                <YAxis tick={{ fill: "#475569", fontSize: 10, fontFamily: "monospace" }}
                  tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<TooltipBox suffix="s elapsed" />} />
                <Area type="monotone" dataKey="pps" name="pages/s" stroke="#22d3ee" strokeWidth={2}
                  fill="url(#ppsFill)" isAnimationActive={false} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
          <BarChart3 className="h-3.5 w-3.5 text-emerald-400" />
          <span className="font-mono text-xs uppercase tracking-wider text-slate-400">HTTP Health</span>
        </div>
        <div className="h-[168px] px-1 py-2" data-testid="http-status-chart">
          {statusData.length === 0 ? (
            <p className="h-full grid place-items-center font-mono text-xs text-slate-600">// no responses yet</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} margin={{ top: 6, right: 12, left: -22, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="key" tick={{ fill: "#475569", fontSize: 10, fontFamily: "monospace" }}
                  tickLine={false} axisLine={{ stroke: "#1e293b" }} />
                <YAxis tick={{ fill: "#475569", fontSize: 10, fontFamily: "monospace" }}
                  tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<TooltipBox suffix="" />} cursor={{ fill: "#1e293b40" }} />
                <Bar dataKey="value" name="responses" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                  {statusData.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThroughputChart;
